// Pre-Approval milestones — TanStack Query hooks for the LA attestation
// surface (applications, letters, final submission). Each milestone
// mutation invalidates its own list query plus the deal detail (so the
// stage / sub-state badges in the tab strip refresh on success).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  preApprovalApplicationsApi,
  preApprovalLettersApi,
  preApprovalSubmissionApi,
} from "@/api/preApproval";
import { dealKeys } from "./useDeals";
import { dealFlowKeys } from "@/hooks/dealFlow/useDealFlow";
import type {
  LinkPreApprovalDocumentRequest,
  PreApprovalSubmissionRequest,
  UpdatePreApprovalNotesRequest,
} from "@/types/deals";

export const preApprovalKeys = {
  applications: (dealId: string) =>
    [...dealKeys.detail(dealId), "pre-approval-applications"] as const,
  letters: (dealId: string) =>
    [...dealKeys.detail(dealId), "pre-approval-letters"] as const,
  readiness: (dealId: string) =>
    [...dealKeys.detail(dealId), "pre-approval-readiness"] as const,
};

function invalidatePreApproval(qc: ReturnType<typeof useQueryClient>, dealId: string) {
  qc.invalidateQueries({ queryKey: preApprovalKeys.applications(dealId) });
  qc.invalidateQueries({ queryKey: preApprovalKeys.letters(dealId) });
  qc.invalidateQueries({ queryKey: preApprovalKeys.readiness(dealId) });
  qc.invalidateQueries({ queryKey: dealKeys.detail(dealId) });
  qc.invalidateQueries({ queryKey: dealKeys.activity(dealId) });
}

// ---------- Applications -------------------------------------------------

export function usePreApprovalApplications(dealId: string | undefined) {
  return useQuery({
    queryKey: preApprovalKeys.applications(dealId ?? ""),
    queryFn: () => preApprovalApplicationsApi.list(dealId!),
    enabled: !!dealId,
  });
}

export function useMarkApplicationGenerated(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => preApprovalApplicationsApi.markGenerated(dealId, id),
    onSuccess: () => invalidatePreApproval(qc, dealId),
  });
}

export function useMarkApplicationSent(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => preApprovalApplicationsApi.markSent(dealId, id),
    onSuccess: () => invalidatePreApproval(qc, dealId),
  });
}

export function useMarkApplicationReceived(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => preApprovalApplicationsApi.markReceived(dealId, id),
    onSuccess: () => invalidatePreApproval(qc, dealId),
  });
}

export function useUpdateApplicationNotes(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: UpdatePreApprovalNotesRequest }) =>
      preApprovalApplicationsApi.updateNotes(dealId, vars.id, vars.body),
    onSuccess: () => invalidatePreApproval(qc, dealId),
  });
}

export function useLinkApplicationDocument(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: LinkPreApprovalDocumentRequest }) =>
      preApprovalApplicationsApi.linkDocument(dealId, vars.id, vars.body),
    onSuccess: () => invalidatePreApproval(qc, dealId),
  });
}

export function useUnlinkApplicationDocument(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => preApprovalApplicationsApi.unlinkDocument(dealId, id),
    onSuccess: () => invalidatePreApproval(qc, dealId),
  });
}

// ---------- Letters ------------------------------------------------------

export function usePreApprovalLetters(dealId: string | undefined) {
  return useQuery({
    queryKey: preApprovalKeys.letters(dealId ?? ""),
    queryFn: () => preApprovalLettersApi.list(dealId!),
    enabled: !!dealId,
  });
}

export function useMarkLetterGenerated(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => preApprovalLettersApi.markGenerated(dealId, id),
    onSuccess: () => invalidatePreApproval(qc, dealId),
  });
}

export function useMarkLetterSent(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => preApprovalLettersApi.markSent(dealId, id),
    onSuccess: () => invalidatePreApproval(qc, dealId),
  });
}

export function useMarkLetterReceivedSigned(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => preApprovalLettersApi.markReceivedSigned(dealId, id),
    onSuccess: () => invalidatePreApproval(qc, dealId),
  });
}

export function useUpdateLetterNotes(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: UpdatePreApprovalNotesRequest }) =>
      preApprovalLettersApi.updateNotes(dealId, vars.id, vars.body),
    onSuccess: () => invalidatePreApproval(qc, dealId),
  });
}

export function useLinkLetterDocument(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: LinkPreApprovalDocumentRequest }) =>
      preApprovalLettersApi.linkDocument(dealId, vars.id, vars.body),
    onSuccess: () => invalidatePreApproval(qc, dealId),
  });
}

export function useUnlinkLetterDocument(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => preApprovalLettersApi.unlinkDocument(dealId, id),
    onSuccess: () => invalidatePreApproval(qc, dealId),
  });
}

// ---------- Submission ---------------------------------------------------

export function usePreApprovalReadiness(dealId: string | undefined) {
  return useQuery({
    queryKey: preApprovalKeys.readiness(dealId ?? ""),
    queryFn: () => preApprovalSubmissionApi.readiness(dealId!),
    enabled: !!dealId,
  });
}

export function useSubmitPreApproval(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PreApprovalSubmissionRequest) =>
      preApprovalSubmissionApi.submit(dealId, body),
    onSuccess: () => {
      invalidatePreApproval(qc, dealId);
      qc.invalidateQueries({ queryKey: dealKeys.lists() });
      // Sending to Underwriting advances the stage, seeds the 7-pill
      // DealAssignment roster and assigns its A1 slot server-side. That roster
      // lives under a different key prefix than the deal detail, so refresh
      // it explicitly or the header A1 pill stays "Unassigned" until staleTime.
      qc.invalidateQueries({ queryKey: dealFlowKeys.assignments(dealId) });
    },
  });
}
